import type { DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { HeaderTdElement, type DetailExpandDescriptor } from "@progress/kendo-react-data-tools";
import { GridColumn, GridToolbar, type GridCustomFilterCellProps, type GridDataStateChangeEvent, type GridDetailRowProps, type GridFilterChangeEvent, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { useInternationalization } from "@progress/kendo-react-intl";
import {
    arrowRotateCwIcon, cancelIcon, infoCircleIcon,
    warningTriangleIcon,
    xCircleIcon,
    type SVGIcon
} from "@progress/kendo-svg-icons";
import { useEffect, useState } from "react";
import { useClearLogMutation, useLazyListAppLogsQuery } from "../../api/admin/adminAppLogApi";
import AppGrid from "../../components/grid/appGrid";
import { DropDownFilterCell } from "../../components/grid/dropDownFilterCell";
import { LogEventLevel } from "../../constants/logLevels";
import type { AppLogDto } from "../../contracts";
import { truncateString } from "../../utils/stringValueConversion";

const levelStyles: Record<string, React.CSSProperties> = {
    verbose: { color: "black" }, // verbose
    debug: { color: "#3F9528" }, // debug
    information: { color: "#3251E9" }, // info
    warning: { color: "#B80CA6" }, // warning
    error: { color: "#8B0000", fontWeight: "bold" }, // error
    fatal: { color: "#FF0000", fontWeight: "bold" } // fatal
};

const logLevelDetails: Record<string, { className: string; icon?: SVGIcon }> = {
    verbose: {
        className: "text-bg-secondary"
    },
    debug: {
        className: "text-bg-dark"
    },
    information: {
        className: "text-bg-primary",
        icon: infoCircleIcon
    },
    warning: {
        className: "text-bg-warning",
        icon: warningTriangleIcon
    },
    error: {
        className: "text-bg-danger",
        icon: xCircleIcon
    },
    fatal: {
        className: "text-bg-danger border border-dark",
        icon: xCircleIcon
    }
};

const LogDetailComponent = (props: GridDetailRowProps) => {
    const intlService = useInternationalization();
    const dataItem = props.dataItem as AppLogDto;
    const level = logLevelDetails[dataItem.level.toLowerCase()] ?? {
        className: "text-bg-secondary"
    };

    const formatJson = (jsonString: string): string => {
        try {
            return JSON.stringify(JSON.parse(jsonString), null, 2);
        } catch {
            return jsonString || "No event data";
        }
    };

    return (
        <section className="bg-body-tertiary p-3">
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-body d-flex align-items-center justify-content-between gap-3 py-3">
                    <span className={`badge d-inline-flex align-items-center gap-2 px-3 py-2 fs-6 ${level.className}`}>
                        {level.icon && <SvgIcon icon={level.icon} />}
                        {dataItem.level}
                    </span>

                    <time className="small text-body-secondary text-nowrap">
                        {intlService.format("{0:d/M/yy h:mm:ss a}", dataItem.time_stamp)}
                    </time>
                </div>

                <div className="card-body d-flex flex-column gap-4">
                    <div>
                        <h3 className="h6 text-body-secondary mb-2">Message</h3>
                        <div className="border-start border-4 ps-3 py-1">
                            <p className="mb-0 text-break">
                                {dataItem.message || "No message provided"}
                            </p>
                        </div>
                    </div>

                    <div>
                        <h3 className="h6 text-body-secondary mb-2">Exception</h3>

                        {dataItem.exception
                            ? (
                                    <div className="alert alert-danger d-flex align-items-start gap-2 mb-0" role="alert">
                                        <SvgIcon icon={xCircleIcon} className="flex-shrink-0 mt-1" />
                                        <pre className="mb-0 text-wrap text-break font-monospace">
                                            {dataItem.exception}
                                        </pre>
                                    </div>
                                )
                            : (
                                    <span className="text-body-secondary">No exception recorded</span>
                                )}
                    </div>

                    <div>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h3 className="h6 text-body-secondary mb-0">Event Data</h3>
                            <span className="badge text-bg-light border">JSON</span>
                        </div>

                        <pre
                            className="bg-dark text-light border rounded-3 p-3 mb-0 overflow-auto font-monospace"
                            style={{
                                maxHeight: "400px",
                                whiteSpace: "pre-wrap",
                                overflowWrap: "anywhere"
                            }}
                        >
                            {formatJson(dataItem.log_event)}
                        </pre>
                    </div>
                </div>
            </div>
        </section>
    );
};

const logEventLevels: string[] = Object.values(LogEventLevel);
const LevelFilterCell = (props: GridCustomFilterCellProps) => (
    <HeaderTdElement columnId={props.thProps?.columnId || ""} {...props.thProps}>
        <DropDownFilterCell {...props} data={logEventLevels} defaultItem="Select Level" />
    </HeaderTdElement>
);

const initialSort: SortDescriptor[] = [{ field: "timeStamp", dir: "desc" }];

const AppLogManagement: React.FC = () => {
    const [triggerListLogsQuery, { data, isLoading }] = useLazyListAppLogsQuery();
    const [triggerClearLogsMutation, { isLoading: isClearLogLoading }] = useClearLogMutation();
    const [detailExpand] = useState<DetailExpandDescriptor>({});

    const [dataState, setDataState] = useState<DataSourceRequestState>({
        skip: 0,
        take: 50,
        sort: initialSort
    });

    const pageChange = (e: GridPageChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            skip: e.page.skip,
            take: e.page.take
        }));
    };

    const dataStateChange = (e: GridDataStateChangeEvent) => {
        setDataState({
            ...e.dataState,
            skip: e.dataState.skip,
            take: e.dataState.take
        });
    };

    const filterChange = (event: GridFilterChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            filter: event.filter
        }));
    };

    const sortChange = (event: GridSortChangeEvent) => {
        setDataState(prev => ({
            ...prev,
            sort: event.sort
        }));
    };

    useEffect(() => {
        triggerListLogsQuery({
            ...dataState
        });
    }, [triggerListLogsQuery, dataState]);

    const logResult = (data?.data ?? []).map(o => ({
        ...o,
        timeStamp: new Date(o.time_stamp)
    }));

    return (
        <div className="d-flex flex-column h-100 min-h-0 overflow-hidden">
            <div className="flex-grow-1 min-h-0 overflow-hidden">
                <AppGrid
                    showLoader={isLoading || isClearLogLoading}
                    dataItemKey="id"
                    scrollable="scrollable"
                    sortable={true}
                    data={logResult}
                    resizable={true}
                    filterable={true}
                    initialState={dataState}
                    onPageChange={pageChange}
                    onDataStateChange={dataStateChange}
                    detail={LogDetailComponent}
                    defaultDetailExpand={detailExpand}
                    filter={dataState.filter}
                    sort={dataState.sort}
                    total={data?.total ?? 0}
                    skip={dataState.skip}
                    take={dataState.take}
                    defaultSort={initialSort}
                    onFilterChange={filterChange}
                    onSortChange={sortChange}
                    pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                    style={{ height: "100%" }}
                >
                    <GridToolbar>
                        <Button
                            svgIcon={cancelIcon}
                            onClick={async () => {
                                if (confirm("Are you sure you want to clear logs?")) {
                                    await triggerClearLogsMutation().unwrap();
                                    triggerListLogsQuery({
                                        ...dataState
                                    });
                                }
                            }}
                        >
                            Clear
                        </Button>
                        <Button
                            svgIcon={arrowRotateCwIcon}
                            onClick={() => {
                                triggerListLogsQuery({
                                    ...dataState
                                });
                            }}
                        >
                            Refresh
                        </Button>
                    </GridToolbar>
                    <GridColumn
                        field="level"
                        title="Level"
                        cells={{
                            filterCell: LevelFilterCell,
                            data: p => {
                                const data = p.dataItem as AppLogDto;
                                const style = levelStyles[data.level.toLowerCase()];

                                if (!style) {
                                    throw new Error(`Unknown level type: ${data.level}`);
                                }

                                return (
                                    <td style={{
                                        ...style,
                                        textAlign: "center"
                                    }}
                                    >
                                        {data.level}
                                    </td>
                                );
                            }
                        }}
                    />
                    <GridColumn
                        field="message"
                        title="Message"
                        filterable={false}
                        cells={{
                            data: p => {
                                const data = p.dataItem as AppLogDto;
                                return <td>{truncateString(data.message, 75)}</td>;
                            }
                        }}
                    />
                    <GridColumn
                        field="exception"
                        title="Exception"
                        filterable={false}
                        cells={{
                            data: p => {
                                const data = p.dataItem as AppLogDto;
                                return <td>{truncateString(data.exception, 75)}</td>;
                            }
                        }}
                    />
                    <GridColumn field="timeStamp" title="TimeStamp" sortable={true} filterable={false} format="{0:d/M/yy h:mm.ss a}" />

                </AppGrid>
            </div>
        </div>
    );
};

export default AppLogManagement;
